import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as storageService from '../../services/storageService';
import { Hospital, ScreenSession } from '../../types';
import {
    ArrowLeft, HeartPulse, Megaphone, Monitor, Tv, Smartphone, Tablet, Laptop,
    Trash2, Sun, Moon, RefreshCw, Edit, X, Check,
    MessageSquare, Star, TrendingUp, ExternalLink, BarChart3,
    Filter, Eye, ThumbsUp, Users, Shield, LogOut as LogOutIcon, Globe,
    CalendarDays
} from 'lucide-react';
import AdManager from '../../components/AdManager';
import { AdCalendar } from '../../components/AdCalendar';
import AdAnalyticsDashboard from '../../components/AdAnalyticsDashboard';
import * as cloudflareService from '../../services/cloudflareService';
import { AdminNavbarUser } from '../../components/AdminNavbarUser';
import MarketingDashboard from '../../components/MarketingDashboard';

type PubTab = 'ads' | 'calendar' | 'monitors' | 'feedback' | 'ad_analytics' | 'marketing';

// ========================== SCREEN EDIT MODAL ==========================
interface EditScreenModalProps {
    screen: ScreenSession;
    primaryColor: string;
    onClose: () => void;
    onSave: (id: string, data: { screenName?: string; forcedTheme?: string | null; screenGroup?: string | null; screenMode?: string; screenModeData?: string | null; forceRefreshTs?: number | null }) => Promise<void>;
    hospital: Hospital;
    onUpdatePreferences: (updates: Partial<HospitalPreferences>) => Promise<boolean>;
}

import { ScreenPlaylistManager } from '../../components/ScreenPlaylistManager';

const EditScreenModal: React.FC<EditScreenModalProps> = ({ screen, primaryColor, onClose, onSave, hospital, onUpdatePreferences }) => {
    const [activeTab, setActiveTab] = useState<'config' | 'playlist'>('config');
    const [name, setName] = useState(screen.screenName || '');
    const [theme, setTheme] = useState<'auto' | 'light' | 'dark'>(
        screen.forcedTheme === 'light' ? 'light' : screen.forcedTheme === 'dark' ? 'dark' : 'auto'
    );
    const [group, setGroup] = useState(screen.screenGroup || '');
    const [mediaUrl, setMediaUrl] = useState(
        screen.screenMode === 'MEDIA_URL' ? (screen.screenModeData || '') : ''
    );
    const [saving, setSaving] = useState(false);

    // Modern State Logic (Matching Super Admin)
    const [editScreenMode, setEditScreenMode] = useState<string>(screen.screenMode || 'BOARD');
    const [editBoardLayout, setEditBoardLayout] = useState<string>(() => {
        const modeData = (screen as any).screenModeData || '';
        return modeData.startsWith('boardLayout:') ? modeData.replace('boardLayout:', '') : 'LIST';
    });

    const handleSave = async () => {
        setSaving(true);
        let screenMode = editScreenMode;
        let screenModeData: string | null = null;

        if (screenMode === 'BOARD' && editBoardLayout !== 'LIST') {
            screenModeData = `boardLayout:${editBoardLayout}`;
        } else if (screenMode === 'MEDIA_URL') {
            screenModeData = mediaUrl.trim() || null;
        }

        await onSave(screen.id, {
            screenName: name.trim() || screen.screenName,
            forcedTheme: theme === 'auto' ? null : theme,
            screenGroup: group.trim() || null,
            screenMode: screenMode as any,
            screenModeData
        });
        setSaving(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-slate-200" onClick={e => e.stopPropagation()}>
                <div className="p-0 border-b border-slate-100 shrink-0 bg-slate-50 flex flex-col">
                    <div className="p-6 pb-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-50 rounded-xl text-indigo-500 border border-indigo-100">
                                <Monitor className="w-5 h-5" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800">Editar Pantalla</h3>
                        </div>
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
                    </div>
                    {/* Tabs */}
                    <div className="flex px-6 gap-6 text-sm font-bold mt-2">
                        <button 
                            onClick={() => setActiveTab('config')}
                            className={`pb-3 border-b-2 transition-all ${activeTab === 'config' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                        >
                            Configuración
                        </button>
                        <button 
                            onClick={() => setActiveTab('playlist')}
                            className={`pb-3 border-b-2 transition-all ${activeTab === 'playlist' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                        >
                            Contenido Exclusivo
                        </button>
                    </div>
                </div>
                
                <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {activeTab === 'playlist' ? (
                        <ScreenPlaylistManager 
                            screen={screen} 
                            hospital={hospital} 
                            primaryColor={primaryColor} 
                            onUpdatePreferences={onUpdatePreferences} 
                        />
                    ) : (
                        <>
                    {/* Live Mini Preview */}
                    <div 
                        className="w-full relative rounded-xl overflow-hidden border border-slate-200 bg-black shadow-inner" 
                        style={{ paddingTop: '56.25%' }}
                        ref={el => {
                            if (el && !el.dataset.observed) {
                                el.dataset.observed = "true";
                                const observer = new ResizeObserver(entries => {
                                    const width = entries[0].contentRect.width;
                                    const iframe = el.querySelector('iframe');
                                    if (iframe) iframe.style.transform = `scale(${width / 1280})`;
                                });
                                observer.observe(el);
                            }
                        }}
                    >
                        <div className="absolute inset-0 w-full h-full overflow-hidden">
                            <iframe
                                src={`/${screen.handle}/board?screenId=${screen.id}&preview=true&themeOverride=${theme === 'auto' ? '' : theme}&modeOverride=${editScreenMode}&layoutOverride=${editBoardLayout}`}
                                className="absolute top-0 left-0 pointer-events-none border-0 max-w-none"
                                style={{ width: '1280px', height: '720px', transformOrigin: '0 0' }}
                                title="Screen Mini Preview"
                            />
                        </div>
                        <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-md px-2 py-1 rounded text-[10px] text-white font-mono flex items-center gap-1.5 z-10 border border-white/10 shadow-lg">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> PREVIEW
                        </div>
                    </div>

                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Nombre de Pantalla</label>
                            <input
                                type="text" value={name} onChange={e => setName(e.target.value)}
                                placeholder={screen.screenName}
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all font-medium"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Grupo / Ubicación</label>
                            <input
                                type="text" value={group} onChange={e => setGroup(e.target.value)}
                                placeholder="Ej: PB, Cafetería, Piso 2"
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all font-medium"
                            />
                        </div>
                    </div>

                    {/* Screen Mode Picker */}
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 block">Modo de Operación</label>
                        <div className="grid grid-cols-4 gap-2">
                            {([
                                { id: 'BOARD', label: 'Médico', desc: 'Pacientes' },
                                { id: 'FULL_ADS', label: 'Promos', desc: 'Slideshow' },
                                { id: 'CLOCK', label: 'Reloj Zen', desc: 'Descanso' },
                                { id: 'MEDIA_URL', label: 'Video', desc: 'YouTube' },
                            ] as const).map(opt => (
                                <button
                                    key={opt.id}
                                    onClick={() => setEditScreenMode(opt.id)}
                                    className={`flex flex-col items-center justify-center gap-1 py-3 px-1 rounded-xl border transition-all text-center ${
                                        editScreenMode === opt.id
                                            ? 'border-indigo-500 bg-indigo-50 text-indigo-600 shadow-sm'
                                            : 'bg-white border-slate-200 text-slate-500 hover:border-indigo-300'
                                    }`}
                                >
                                    <span className="text-[11px] font-black uppercase tracking-wide leading-none">{opt.label}</span>
                                    <span className="text-[9px] opacity-60 leading-none mt-1">{opt.desc}</span>
                                </button>
                            ))}
                        </div>
                        
                        {/* URL Input for MEDIA_URL mode */}
                        {editScreenMode === 'MEDIA_URL' && (
                            <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">URL del Video (YouTube / MP4)</label>
                                <input
                                    type="text" value={mediaUrl} onChange={e => setMediaUrl(e.target.value)}
                                    placeholder="https://youtube.com/watch?v=..."
                                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                                />
                            </div>
                        )}
                    </div>

                    {/* Board Layout Picker (Only if BOARD mode) */}
                    {editScreenMode === 'BOARD' && (
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 block">Layout del Tablero</label>
                        <div className="grid grid-cols-5 gap-2">
                            {([
                                { id: 'LIST', label: 'Lista', desc: 'Clásico' },
                                { id: 'GRID_2COL', label: 'Grid', desc: 'Cards' },
                                { id: 'SIDEBAR_AD', label: 'Con Ad', desc: '70/30' },
                                { id: 'FOCUS_SINGLE', label: 'Foco', desc: 'Individual' },
                                { id: 'TICKER_ONLY', label: 'Ticker', desc: 'Marquesina' },
                            ] as const).map(opt => (
                                <button
                                    key={opt.id}
                                    onClick={() => setEditBoardLayout(opt.id)}
                                    className={`flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl border transition-all text-center ${
                                        editBoardLayout === opt.id
                                            ? 'border-indigo-500 bg-indigo-50 text-indigo-600 shadow-sm'
                                            : 'bg-white border-slate-200 text-slate-500 hover:border-indigo-300'
                                    }`}
                                >
                                    <span className="text-[10px] font-black uppercase tracking-wide leading-none">{opt.label}</span>
                                    <span className="text-[8px] opacity-60 leading-none mt-1">{opt.desc}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    )}

                    {/* Forced Theme */}
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 block">Tema de la Pantalla</label>
                        <div className="flex bg-slate-100 rounded-xl p-1 relative">
                            <div className="absolute top-1 bottom-1 w-1/3 bg-white rounded-lg shadow-sm transition-all duration-300" style={{ left: theme === 'auto' ? '0.25rem' : theme === 'dark' ? 'calc(33.333% + 0.25rem)' : 'calc(66.666% - 0.25rem)' }} />
                            
                            <button onClick={() => setTheme('auto')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold relative z-10 transition-colors ${theme === 'auto' ? 'text-indigo-600' : 'text-slate-500'}`}>
                                Auto
                            </button>
                            <button onClick={() => setTheme('dark')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold relative z-10 transition-colors ${theme === 'dark' ? 'text-indigo-600' : 'text-slate-500'}`}>
                                <Moon className="w-4 h-4" /> Oscuro
                            </button>
                            <button onClick={() => setTheme('light')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold relative z-10 transition-colors ${theme === 'light' ? 'text-indigo-600' : 'text-slate-500'}`}>
                                <Sun className="w-4 h-4" /> Claro
                            </button>
                        </div>
                    </div>

                    {/* Device info (read-only) */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Telemetría del Dispositivo</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="text-slate-500">Sistema</div>
                            <div className="font-bold text-slate-800 text-right">{screen.os || '—'}</div>
                            <div className="text-slate-500">Navegador</div>
                            <div className="font-bold text-slate-800 text-right">{screen.browser || '—'}</div>
                            <div className="text-slate-500">IP de Red</div>
                            <div className="font-mono font-bold text-slate-800 text-right text-[10px]">{screen.ipAddress || '—'}</div>
                            <div className="text-slate-500">Resolución Nativa</div>
                            <div className="font-mono font-bold text-slate-800 text-right">{screen.resolution || '—'}</div>
                        </div>
                    </div>
                    </>
                    )}
                </div>

                <div className="p-6 border-t border-slate-100 flex gap-3 bg-white">
                    <button onClick={onClose} className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold text-sm transition-all">Cancelar</button>
                    <button
                        onClick={handleSave} disabled={saving}
                        className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-indigo-200 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
                        Guardar Cambios
                    </button>
                </div>
            </div>
        </div>
    );
};

interface AdminPublicidadProps {
    activeSubTab: PubTab;
}

const AdminPublicidad: React.FC<AdminPublicidadProps> = ({ activeSubTab }) => {
    const navigate = useNavigate();
    const [hospital, setHospital] = useState<Hospital | null>(null);
    const [loading, setLoading] = useState(true);
    const [screens, setScreens] = useState<ScreenSession[]>([]);
    const [globalAssets, setGlobalAssets] = useState<AdAsset[]>([]);
    const [editingScreen, setEditingScreen] = useState<ScreenSession | null>(null);
    const [feedback, setFeedback] = useState<any[]>([]);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [feedbackStats, setFeedbackStats] = useState<any | null>(null);
    const [selectedRatingFilter, setSelectedRatingFilter] = useState<number | null>(null);
    const [previewScreen, setPreviewScreen] = useState<ScreenSession | null>(null);

    // Staff Sessions state
    const [staffSessions, setStaffSessions] = useState<any[]>([]);
    const [staffSessionsLoading, setStaffSessionsLoading] = useState(false);
    const [revokingId, setRevokingId] = useState<string | null>(null);
    
    // Pairing State
    const [pairingModalOpen, setPairingModalOpen] = useState(false);
    const [pairingPinInput, setPairingPinInput] = useState('');
    const [pairingError, setPairingError] = useState('');
    const [pairingSuccess, setPairingSuccess] = useState(false);
    const [pairingLoading, setPairingLoading] = useState(false);

    const auth = storageService.getAuthData();
    const { hospitalId: urlHospitalId } = useParams<{ hospitalId: string }>();
    const resolvedHospitalId = urlHospitalId || auth?.hospitalId;
    const isStrictAdmin = !auth?.role || ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN'].includes(auth.role) || ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN'].includes(auth?.staffRole as string);

    useEffect(() => {
        const load = async () => {
            if (!auth && !resolvedHospitalId) { navigate('/login'); return; }
            if (!resolvedHospitalId) { navigate('/login'); return; }
            const isImpersonating = window.location.search.includes('impersonate=true');
            if (!isImpersonating && (resolvedHospitalId === 'ADMIN' || auth?.role === 'SUPER_ADMIN')) {
                navigate('/admin/dashboard');
                return;
            }
            if (!isStrictAdmin) {
                navigate('/staff/paneladmin/analytics');
                return;
            }
            
            const found = await storageService.getHospitalById(resolvedHospitalId);
            setHospital(found || null);

            if (found?.groupId) {
                try {
                    const prefs = await cloudflareService.getGroupPreferences(found.groupId);
                    if (prefs?.globalAdAssets) {
                        setGlobalAssets(prefs.globalAdAssets);
                    }
                } catch (e) { console.error("Error loading global assets", e); }
            }

            try {
                const screenData = await storageService.getScreenSessions(resolvedHospitalId);
                setScreens(screenData as ScreenSession[]);
            } catch (err: any) {
                if (err.message === 'UNAUTHORIZED') {
                    await storageService.logout();
                    navigate('/login');
                    return;
                }
                setScreens([]);
            }

            setLoading(false);
        };
        load();
    }, []);

    // Load feedback + stats when tab is activated
    useEffect(() => {
        if (activeSubTab !== 'feedback' || !resolvedHospitalId) return;
        const loadFeedback = async () => {
            setFeedbackLoading(true);
            try {
                const [data, stats] = await Promise.all([
                    cloudflareService.getFeedback(resolvedHospitalId),
                    cloudflareService.getFeedbackStats(resolvedHospitalId)
                ]);
                setFeedback(data);
                setFeedbackStats(stats);
            } catch (err: any) {
                if (err.message === 'UNAUTHORIZED') {
                    await storageService.logout();
                    navigate('/login');
                    return;
                }
                setFeedback([]);
                setFeedbackStats(null);
            }
            setFeedbackLoading(false);
        };
        loadFeedback();
    }, [activeSubTab, resolvedHospitalId]);

    // Load staff sessions when tab is activated
    useEffect(() => {
        if (activeSubTab !== 'staff' as any || !resolvedHospitalId) return;
        const loadSessions = async () => {
            setStaffSessionsLoading(true);
            try {
                const data = await cloudflareService.getStaffSessions(resolvedHospitalId);
                setStaffSessions(data);
            } catch (err: any) {
                if (err.message === 'UNAUTHORIZED') {
                    await storageService.logout();
                    navigate('/login');
                    return;
                }
                setStaffSessions([]);
            }
            setStaffSessionsLoading(false);
        };
        loadSessions();
    }, [activeSubTab, resolvedHospitalId]);

    const handleRevokeSession = async (sessionId: string) => {
        if (!window.confirm('¿Revocar esta sesión? El usuario tendrá que volver a iniciar sesión.')) return;
        setRevokingId(sessionId);
        try {
            await cloudflareService.revokeStaffSession(sessionId);
            setStaffSessions(prev => prev.filter(s => s.id !== sessionId));
        } catch (err) {
            console.error('Failed to revoke session', err);
        }
        setRevokingId(null);
    };

    const filteredFeedback = useMemo(() => {
        if (!selectedRatingFilter) return feedback;
        return feedback.filter((fb: any) => fb.rating === selectedRatingFilter);
    }, [feedback, selectedRatingFilter]);

    const refreshScreens = async () => {
        if (!resolvedHospitalId) return;
        try {
            const data = await storageService.getScreenSessions(resolvedHospitalId);
            setScreens(data as ScreenSession[]);
        } catch (err: any) {
            if (err.message === 'UNAUTHORIZED') {
                await storageService.logout();
                navigate('/login');
                return;
            }
            setScreens([]);
        }
    };

    const handleUpdateScreen = async (id: string, data: { screenName?: string; forcedTheme?: string | null; screenGroup?: string | null; screenMode?: string; screenModeData?: string | null; forceRefreshTs?: number | null }) => {
        await storageService.updateScreenSession(id, data);
        await refreshScreens();
    };

    if (loading) return null;
    if (!hospital) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Hospital no configurado</h1>
                <button onClick={() => {
                    if (auth?.role === 'GROUP_ADMIN') navigate('/c/' + (auth.groupId));
                    else navigate('/staff/paneladmin');
                }} className="text-indigo-600 font-bold hover:underline">Volver</button>
            </div>
        );
    }

    const primaryColor = hospital.branding?.primaryColor || '#5B65DC';

    const handlePairSubmit = async () => {
        if (!pairingPinInput || pairingPinInput.length !== 6) {
            setPairingError('El código debe ser de 6 dígitos.');
            return;
        }
        setPairingLoading(true);
        setPairingError('');
        const ok = await cloudflareService.approveScreenPairing(pairingPinInput);
        if (ok) {
            setPairingSuccess(true);
            setTimeout(() => {
                setPairingModalOpen(false);
                refreshScreens();
            }, 2000);
        } else {
            setPairingError('Código inválido o expirado.');
        }
        setPairingLoading(false);
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans">
            {/* ─── Content ──────────────────────────────────── */}
            {activeSubTab === 'marketing' ? (
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                    <MarketingDashboard
                        theme="light"
                        hospitals={[hospital]}
                        hospitalId={hospital.id}
                        adAssets={globalAssets} // Could be empty, but it's fine
                        initialTab="ads_local"
                    />
                </main>
            ) : activeSubTab === 'ads' ? (
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                    <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 sm:p-8">
                        <AdManager
                            hospital={hospital}
                            onUpdate={(updatedHospital: Hospital) => {
                                setHospital(updatedHospital);
                                // Refresh screens in case something changed that affects list
                                refreshScreens();
                            }}
                            screens={screens}
                            globalAssets={globalAssets}
                        />
                    </div>
                </main>
            ) : activeSubTab === 'calendar' ? (
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                    <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 sm:p-8">
                        <AdCalendar
                            hospital={hospital}
                            onUpdate={(updatedHospital: Hospital) => {
                                setHospital(updatedHospital);
                                refreshScreens();
                            }}
                            screens={screens}
                        />
                    </div>
                </main>
            ) : activeSubTab === 'monitors' ? (
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                    {/* Monitor Management */}
                    <div className="mb-6 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}12` }}>
                                <Monitor className="w-5 h-5" style={{ color: primaryColor }} />
                            </div>
                            <div>
                                <h2 className="text-lg font-black text-slate-800 tracking-tight">Pantallas del Hospital</h2>
                                <p className="text-xs text-slate-400 font-medium">
                                    {screens.filter(s => s.isOnline).length} online de {screens.length} registrada{screens.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setPairingModalOpen(true);
                                    setPairingError('');
                                    setPairingPinInput('');
                                    setPairingSuccess(false);
                                }}
                                className="flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl transition-all active:scale-95 text-white shadow-lg"
                                style={{ backgroundColor: primaryColor }}
                            >
                                <Monitor className="w-4 h-4" />
                                <span className="hidden sm:inline">Ligar Pantalla</span>
                            </button>
                            <button
                                onClick={refreshScreens}
                                className="flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all active:scale-95"
                                style={{ color: primaryColor }}
                            >
                                <RefreshCw className="w-4 h-4" />
                                <span className="hidden sm:inline">Actualizar</span>
                            </button>
                        </div>
                    </div>

                    {screens.length === 0 ? (
                        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-12 text-center">
                            <Monitor className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500 font-bold mb-1">Sin pantallas registradas</p>
                            <p className="text-slate-400 text-sm">Las pantallas se registran automáticamente al abrir el tablero público del hospital.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {screens.map(screen => {
                                const minutesAgo = Math.round((Date.now() - new Date(screen.lastHeartbeat).getTime()) / 60000);
                                const timeLabel = minutesAgo < 1 ? 'Ahora'
                                    : minutesAgo < 60 ? `hace ${minutesAgo}m`
                                        : minutesAgo < 1440 ? `hace ${Math.round(minutesAgo / 60)}h`
                                            : `hace ${Math.round(minutesAgo / 1440)}d`;

                                return (
                                    <div
                                        key={screen.id}
                                        className={`bg-white rounded-2xl border ${screen.isOnline ? 'border-emerald-200' : 'border-slate-100'} p-5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] transition-all group relative overflow-hidden hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]`}
                                    >
                                        {screen.isOnline && <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 blur-[50px] rounded-full pointer-events-none" />}

                                        <div className="flex items-start justify-between mb-4 relative z-10">
                                            <div className="flex items-center gap-3">
                                                <div className={`relative p-2.5 rounded-xl border ${screen.isOnline
                                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                                                    : 'bg-slate-50 border-slate-100 text-slate-400'
                                                    }`}>
                                                    {(() => {
                                                        const combined = `${screen.os || ''} ${screen.browser || ''} ${screen.userAgent || ''}`.toLowerCase();
                                                        let Icon = Monitor;
                                                        if (combined.includes('tizen') || combined.includes('webos') || combined.match(/\btv\b/) || combined.includes('roku') || combined.includes('chromecast')) Icon = Tv;
                                                        else if ((combined.includes('android') && combined.includes('mobile')) || combined.includes('iphone')) Icon = Smartphone;
                                                        else if (combined.includes('ipad') || (combined.includes('android') && !combined.includes('mobile')) || combined.includes('tablet')) Icon = Tablet;
                                                        else if (combined.includes('mac')) Icon = Laptop;
                                                        return <Icon className="w-5 h-5" />;
                                                    })()}
                                                    <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${screen.isOnline ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]' : 'bg-red-400'}`}>
                                                        {screen.isOnline && <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />}
                                                    </span>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-slate-800 text-sm">{screen.screenName}</h4>
                                                    <p className="text-[10px] text-slate-400">{screen.handle}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                <button
                                                    onClick={async () => {
                                                        const btn = document.getElementById(`sync-${screen.id}`);
                                                        if (btn) btn.classList.add('animate-spin');
                                                        await handleUpdateScreen(screen.id, { forceRefreshTs: Date.now() });
                                                        setTimeout(() => { if (btn) btn.classList.remove('animate-spin') }, 1000);
                                                    }}
                                                    className="p-1.5 rounded-lg text-slate-300 hover:text-emerald-500 hover:bg-emerald-50 transition-all"
                                                    title="Forzar Recarga Completa (Hard Refresh)"
                                                >
                                                    <RefreshCw id={`sync-${screen.id}`} className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => setPreviewScreen(screen)}
                                                    className="p-1.5 rounded-lg text-slate-300 hover:text-indigo-500 hover:bg-indigo-50 transition-all"
                                                    title="Previsualizar"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => setEditingScreen(screen)}
                                                    className="p-1.5 rounded-lg text-slate-300 hover:text-blue-500 hover:bg-blue-50 transition-all"
                                                    title="Editar"
                                                >
                                                    <Edit className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        if (window.confirm(`¿Eliminar "${screen.screenName}"?`)) {
                                                            await storageService.deleteScreenSession(screen.id);
                                                            setScreens(prev => prev.filter(s => s.id !== screen.id));
                                                        }
                                                    }}
                                                    className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="space-y-2 text-xs relative z-10">
                                            <div className="flex justify-between text-slate-400">
                                                <span>Dispositivo</span>
                                                <span className="font-bold text-slate-600 truncate max-w-[140px] text-right" title={`${screen.os || 'Desconocido'} • ${screen.browser || '—'}`}>
                                                    {screen.os || 'Desconocido'} <span className="text-[10px] text-slate-400">({screen.browser?.split(' ')[0] || '—'})</span>
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-slate-400">
                                                <span>Resolución</span>
                                                <span className="font-mono font-bold text-slate-600">{screen.resolution || '—'}</span>
                                            </div>
                                            <div className="flex justify-between text-slate-400">
                                                <span>Grupo Funcional</span>
                                                <span className="font-bold text-slate-600">{screen.screenGroup || 'Sin asignar'}</span>
                                            </div>
                                            <div className="flex justify-between text-slate-400">
                                                <span>Modo</span>
                                                <span className="font-bold" style={{ color: primaryColor }}>{
                                                    {
                                                        'BOARD': 'Tablero Pacientes',
                                                        'FULL_ADS': 'Publicidad Full',
                                                        'CLOCK': 'Reloj Zen',
                                                        'MEDIA_URL': 'Multimedia / Video'
                                                    }[screen.screenMode || 'BOARD'] || 'Desconocido'
                                                }</span>
                                            </div>
                                            <div className="flex justify-between text-slate-400">
                                                <span>Tema</span>
                                                <span className="font-bold text-slate-600 flex items-center gap-1">
                                                    {screen.theme === 'dark' ? <><Moon className="w-3 h-3" /> Oscuro</> : <><Sun className="w-3 h-3" /> Claro</>}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-slate-400">
                                                <span>Heartbeat</span>
                                                <span className={`font-bold ${screen.isOnline ? 'text-emerald-500' : 'text-red-400'}`}>{timeLabel}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </main>

            ) : activeSubTab === 'feedback' ? (
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">

                    {feedbackLoading ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5 animate-pulse">
                                        <div className="h-3 bg-slate-100 rounded w-20 mb-3" />
                                        <div className="h-8 bg-slate-100 rounded w-16 mb-2" />
                                        <div className="h-2 bg-slate-50 rounded w-24" />
                                    </div>
                                ))}
                            </div>
                            <div className="bg-white rounded-2xl border border-slate-100 p-6 animate-pulse">
                                <div className="h-4 bg-slate-100 rounded w-40 mb-6" />
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} className="flex items-center gap-3 mb-3">
                                        <div className="h-3 bg-slate-100 rounded w-8" />
                                        <div className="h-4 bg-slate-50 rounded flex-1" />
                                        <div className="h-3 bg-slate-100 rounded w-10" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* ── KPI Cards ─────────────────────────── */}
                            {feedbackStats && (
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                                    {/* Average Rating */}
                                    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 relative overflow-hidden group hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)] transition-all">
                                        <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-[40px] opacity-20" style={{ backgroundColor: '#FBBF24' }} />
                                        <div className="flex items-center gap-2 mb-2">
                                            <Star className="w-4 h-4 text-amber-400" fill="#FBBF24" />
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Rating Promedio</span>
                                        </div>
                                        <p className="text-3xl font-black text-slate-800 tracking-tight">
                                            {feedbackStats.averageRating > 0 ? feedbackStats.averageRating.toFixed(1) : '—'}
                                            <span className="text-sm font-bold text-slate-300 ml-1">/5</span>
                                        </p>
                                        {feedbackStats.recentTrend?.length >= 2 && (() => {
                                            const diff = feedbackStats.recentTrend[0].avg - feedbackStats.recentTrend[1].avg;
                                            return diff !== 0 ? (
                                                <p className={`text-[10px] font-bold mt-1 ${diff > 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                                                    {diff > 0 ? '▲' : '▼'} {Math.abs(diff).toFixed(1)} vs semana anterior
                                                </p>
                                            ) : null;
                                        })()}
                                    </div>

                                    {/* Total Reviews */}
                                    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 relative overflow-hidden group hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)] transition-all">
                                        <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-[40px] opacity-20" style={{ backgroundColor: primaryColor }} />
                                        <div className="flex items-center gap-2 mb-2">
                                            <BarChart3 className="w-4 h-4" style={{ color: primaryColor }} />
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total Reseñas</span>
                                        </div>
                                        <p className="text-3xl font-black text-slate-800 tracking-tight">{feedbackStats.totalReviews}</p>
                                        {feedbackStats.recentTrend?.[0] && (
                                            <p className="text-[10px] font-bold text-slate-400 mt-1">
                                                +{feedbackStats.recentTrend[0].count} esta semana
                                            </p>
                                        )}
                                    </div>

                                    {/* Google Clicks */}
                                    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 relative overflow-hidden group hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)] transition-all">
                                        <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-[40px] opacity-10 bg-blue-500" />
                                        <div className="flex items-center gap-2 mb-2">
                                            <ExternalLink className="w-4 h-4 text-blue-500" />
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Clicks Google</span>
                                        </div>
                                        <p className="text-3xl font-black text-slate-800 tracking-tight">{feedbackStats.googleReviewClicks}</p>
                                        <p className="text-[10px] font-bold text-slate-400 mt-1">Redirigidos a Google Reviews</p>
                                    </div>

                                    {/* Conversion Rate */}
                                    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 relative overflow-hidden group hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)] transition-all">
                                        <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-[40px] opacity-10 bg-emerald-500" />
                                        <div className="flex items-center gap-2 mb-2">
                                            <TrendingUp className="w-4 h-4 text-emerald-500" />
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Conversión</span>
                                        </div>
                                        <p className="text-3xl font-black text-slate-800 tracking-tight">
                                            {feedbackStats.conversionRate > 0 ? `${feedbackStats.conversionRate}%` : '—'}
                                        </p>
                                        <p className="text-[10px] font-bold text-slate-400 mt-1">5★ → Google Reviews</p>
                                    </div>
                                </div>
                            )}

                            {/* ── Star Distribution + Reviews Funnel ──────── */}
                            {feedbackStats && feedbackStats.totalReviews > 0 && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                                    {/* Star Distribution */}
                                    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6">
                                        <h3 className="text-sm font-black text-slate-800 tracking-tight mb-5 flex items-center gap-2">
                                            <Star className="w-4 h-4 text-amber-400" fill="#FBBF24" />
                                            Distribución de Calificaciones
                                        </h3>
                                        <div className="space-y-3">
                                            {[5, 4, 3, 2, 1].map(star => {
                                                const count = feedbackStats.distribution[String(star)] || 0;
                                                const pct = feedbackStats.totalReviews > 0 ? Math.round((count / feedbackStats.totalReviews) * 100) : 0;
                                                return (
                                                    <button
                                                        key={star}
                                                        onClick={() => setSelectedRatingFilter(selectedRatingFilter === star ? null : star)}
                                                        className={`w-full flex items-center gap-3 group/bar transition-all rounded-lg px-2 py-1 -mx-2 ${selectedRatingFilter === star ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
                                                    >
                                                        <span className="text-xs font-bold text-slate-500 w-5 text-right shrink-0">{star}★</span>
                                                        <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden relative">
                                                            <div
                                                                className="h-full rounded-full transition-all duration-700 ease-out"
                                                                style={{
                                                                    width: `${Math.max(pct, 2)}%`,
                                                                    backgroundColor: star >= 4 ? '#10B981' : star === 3 ? '#F59E0B' : '#EF4444'
                                                                }}
                                                            />
                                                        </div>
                                                        <span className="text-xs font-bold text-slate-600 w-12 text-right shrink-0">
                                                            {count} <span className="text-slate-300 font-medium">({pct}%)</span>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Reviews Funnel */}
                                    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6">
                                        <h3 className="text-sm font-black text-slate-800 tracking-tight mb-5 flex items-center gap-2">
                                            <ThumbsUp className="w-4 h-4" style={{ color: primaryColor }} />
                                            Reviews Funnel
                                        </h3>
                                        <div className="space-y-3">
                                            {[
                                                { label: 'Total de Reseñas', value: feedbackStats.totalReviews, color: primaryColor },
                                                { label: 'Calificaron 5★', value: feedbackStats.fiveStarCount, color: '#10B981' },
                                                { label: 'Clicks a Google', value: feedbackStats.googleReviewClicks, color: '#3B82F6' },
                                            ].map((item, i) => {
                                                const maxVal = feedbackStats.totalReviews || 1;
                                                const pct = Math.round((item.value / maxVal) * 100);
                                                return (
                                                    <div key={i}>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className="text-xs font-bold text-slate-600">{item.label}</span>
                                                            <span className="text-xs font-black text-slate-800">{item.value}</span>
                                                        </div>
                                                        <div className="bg-slate-100 rounded-full h-3 overflow-hidden">
                                                            <div
                                                                className="h-full rounded-full transition-all duration-700 ease-out"
                                                                style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: item.color }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {feedbackStats.totalReviews > 0 && (
                                            <div className="mt-5 pt-4 border-t border-slate-100">
                                                <div className="bg-slate-50 rounded-xl p-4">
                                                    <p className="text-xs text-slate-600 leading-relaxed">
                                                        {feedbackStats.fiveStarCount > 0 ? (
                                                            <>
                                                                <span className="font-black" style={{ color: primaryColor }}>
                                                                    {Math.round((feedbackStats.fiveStarCount / feedbackStats.totalReviews) * 100)}%
                                                                </span> de los familiares califican con 5★
                                                                {feedbackStats.googleReviewClicks > 0 && feedbackStats.fiveStarCount > 0 && (
                                                                    <> y el <span className="font-black text-blue-600">
                                                                        {Math.round((feedbackStats.googleReviewClicks / feedbackStats.fiveStarCount) * 100)}%
                                                                    </span> de ellos llegan a Google Reviews.
                                                                    Las calificaciones negativas se quedan <span className="font-bold">privadas</span>.
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>Aún no hay calificaciones de 5★. Las reseñas negativas se quedan <span className="font-bold">privadas</span> para mejora interna.</>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ── Feedback List ─────────────────────── */}
                            <div className="mb-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}12` }}>
                                        <MessageSquare className="w-5 h-5" style={{ color: primaryColor }} />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-black text-slate-800 tracking-tight">Comentarios de Familiares</h2>
                                        <p className="text-xs text-slate-400 font-medium">
                                            {selectedRatingFilter ? (
                                                <>{filteredFeedback.length} de {feedback.length} — filtrando {selectedRatingFilter}★</>
                                            ) : (
                                                <>{feedback.length} comentario{feedback.length !== 1 ? 's' : ''} recibido{feedback.length !== 1 ? 's' : ''}</>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                {selectedRatingFilter && (
                                    <button
                                        onClick={() => setSelectedRatingFilter(null)}
                                        className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all"
                                        style={{ color: primaryColor }}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                        Limpiar Filtro
                                    </button>
                                )}
                            </div>

                            {feedback.length === 0 ? (
                                <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-12 text-center">
                                    <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                    <p className="text-slate-500 font-bold mb-1">Sin feedback aún</p>
                                    <p className="text-slate-400 text-sm">Cuando los familiares califiquen su experiencia, sus comentarios aparecerán aquí.</p>
                                </div>
                            ) : filteredFeedback.length === 0 ? (
                                <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-12 text-center">
                                    <Filter className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                    <p className="text-slate-500 font-bold mb-1">Sin resultados para {selectedRatingFilter}★</p>
                                    <p className="text-slate-400 text-sm">No hay calificaciones con este filtro.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {filteredFeedback.map((fb: any) => {
                                        const date = new Date(fb.createdAt);
                                        const timeStr = date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                                        const ratingColor = fb.rating >= 4 ? '#10B981' : fb.rating === 3 ? '#F59E0B' : '#EF4444';
                                        return (
                                            <div key={fb.id} className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)] transition-shadow">
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex items-center gap-0.5">
                                                            {[1, 2, 3, 4, 5].map(s => (
                                                                <Star
                                                                    key={s}
                                                                    className="w-4 h-4"
                                                                    fill={s <= fb.rating ? '#FBBF24' : 'transparent'}
                                                                    stroke={s <= fb.rating ? '#FBBF24' : '#E2E8F0'}
                                                                    strokeWidth={1.5}
                                                                />
                                                            ))}
                                                        </div>
                                                        <span
                                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black border"
                                                            style={{
                                                                backgroundColor: `${ratingColor}10`,
                                                                borderColor: `${ratingColor}25`,
                                                                color: ratingColor
                                                            }}
                                                        >
                                                            {fb.rating}/5
                                                        </span>
                                                    </div>
                                                    <span className="text-[10px] text-slate-400 font-medium">{timeStr}</span>
                                                </div>
                                                {fb.message ? (
                                                    <p className="text-sm text-slate-700 leading-relaxed">{fb.message}</p>
                                                ) : (
                                                    <p className="text-sm text-slate-400 italic">Sin comentario escrito</p>
                                                )}
                                                {fb.patientCode && (
                                                    <p className="mt-2 text-[10px] text-slate-300 font-mono">Código: {fb.patientCode}</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </main>
            ) : activeSubTab === 'ad_analytics' ? (
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                    <AdAnalyticsDashboard
                        theme="light"
                        hospitalId={resolvedHospitalId}
                        adAssets={hospital?.preferences?.adAssets || []}
                    />
                </main>
            ) : null}

            {/* ─── Edit Screen Modal ────────────────────────── */}
            {editingScreen && (
                <EditScreenModal
                    screen={editingScreen}
                    primaryColor={primaryColor}
                    onClose={() => setEditingScreen(null)}
                    onSave={handleUpdateScreen}
                    hospital={hospital!}
                    onUpdatePreferences={async (updates) => {
                        if (!hospital) return false;
                        const newPrefs = { ...hospital.preferences, ...updates };
                        try {
                            const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8787' : '';
                            const res = await fetch(`${baseUrl}/api/hospitals/${hospital.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ preferences: newPrefs })
                            });
                            if (res.ok) {
                                setHospital({ ...hospital, preferences: newPrefs });
                                return true;
                            }
                        } catch (e) { console.error(e); }
                        return false;
                    }}
                />
            )}

            {/* ─── Preview Screen Modal ───────────────────────── */}
            {previewScreen && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[100] flex flex-col items-center justify-center p-4 sm:p-8 animate-fade-in">
                    <div className="w-full max-w-6xl flex justify-between items-center mb-4">
                        <div>
                            <h3 className="text-white font-bold text-lg flex items-center gap-2">
                                <Monitor className="w-5 h-5 text-indigo-400" />
                                {previewScreen.screenName}
                            </h3>
                            <p className="text-white/50 text-sm">Transmisión en vivo de la pantalla ({previewScreen.resolution || 'Auto'})</p>
                        </div>
                        <button onClick={() => setPreviewScreen(null)} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-all">
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="w-full max-w-6xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl relative border border-white/10">
                        <iframe
                            src={`/${previewScreen.handle}/board?screenId=${previewScreen.id}`}
                            className="w-full h-full border-0 pointer-events-none"
                            title="Screen Preview"
                        />
                    </div>
                    <div className="mt-4 flex gap-4">
                        <p className="text-white/40 text-xs font-mono uppercase tracking-widest flex items-center justify-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> LIVE STREAM
                        </p>
                    </div>
                </div>
            )}

            {/* ─── Pairing Modal ────────────────────────────── */}
            {pairingModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex flex-col items-center justify-center p-4">
                    <div className="bg-white max-w-sm w-full rounded-3xl shadow-2xl overflow-hidden animate-fade-in border border-slate-100">
                        <div className="p-6 text-center">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                                <Monitor className="w-8 h-8" style={{ color: primaryColor }} />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight mb-3">Ligar Nueva Pantalla</h3>
                            <div className="bg-slate-50 rounded-xl p-4 mb-5 border border-slate-100 text-left shadow-inner">
                                <p className="text-[11px] font-bold tracking-wide uppercase text-slate-400 mb-1.5">Paso 1: Abrir en la TV</p>
                                <p className="text-xs font-mono font-bold text-indigo-600 bg-white border border-indigo-100 px-3 py-2 rounded-lg text-center select-all shadow-sm break-all">
                                    hospital-status.com/{hospital.handle}/board
                                </p>
                                <div className="h-px bg-slate-200 my-3 relative">
                                    <span className="absolute left-1/2 -top-2 flex shrink-0 justify-center w-6 tracking-widest bg-slate-50 text-[10px] text-slate-400 font-bold uppercase transition-transform select-none">+</span>
                                </div>
                                <p className="text-[11px] font-bold tracking-wide uppercase text-slate-400 mb-1">Paso 2: Ingresar Código</p>
                                <p className="text-xs text-slate-500 font-medium leading-relaxed">Escribe aquí el PIN numérico de 6 dígitos que aparezca en la pantalla bloqueada.</p>
                            </div>

                            {pairingSuccess ? (
                                <div className="bg-emerald-50 text-emerald-600 rounded-2xl p-6 flex flex-col items-center">
                                    <Check className="w-10 h-10 mb-2 text-emerald-500" />
                                    <p className="font-bold">Pantalla Ligada</p>
                                    <p className="text-xs text-center mt-1">Configurando dispositivo...</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <input
                                        type="text"
                                        value={pairingPinInput}
                                        onChange={e => {
                                            setPairingError('');
                                            // Force upper case and limit to 6 digits/letters just in case
                                            setPairingPinInput(e.target.value.replace(/[^0-9A-Z]/gi, '').toUpperCase().slice(0, 6));
                                        }}
                                        placeholder="000000"
                                        className="w-full text-center text-3xl font-black tracking-[0.25em] bg-slate-50 border-2 border-slate-200 rounded-2xl py-4 focus:outline-none focus:border-indigo-500 uppercase placeholder:text-slate-300 transition-all font-mono"
                                        autoFocus
                                        disabled={pairingLoading}
                                    />
                                    {pairingError && <p className="text-red-500 text-sm font-bold bg-red-50 p-2 rounded-lg">{pairingError}</p>}
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setPairingModalOpen(false)}
                                            className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl transition-all disabled:opacity-50"
                                            disabled={pairingLoading}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handlePairSubmit}
                                            disabled={pairingPinInput.length !== 6 || pairingLoading}
                                            className="flex-1 py-3 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-[0.98]"
                                            style={{ backgroundColor: primaryColor }}
                                        >
                                            {pairingLoading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Confirmar'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPublicidad;
